import { styled } from 'baseui'
import {
  COMPACT_MODAL_MEDIA,
  COMPACT_PRIMARY_COLUMN_STYLE,
  COMPACT_MEDIA_BANNER_STYLE,
  ModalCloseButton,
  paddingLonghands,
} from '@/components/Modal/ResponsiveModalLayout'
import SlidingImages, { SlideSource } from './SlidingImages'
import { lightTheme, premiumBrandGradient, superBrandGradient } from '@/customTheme'
import Icons from '../../../Icons'
import ButtonCustom from '@/components/ButtonCustom'
import { SizeButton } from '@/constants/sizeButton'
import { KIND } from 'baseui/button'
import premiumTile1 from '@assets/images/congrats-premium/tile-1.jpg'
import premiumTile2 from '@assets/images/congrats-premium/tile-2.jpg'
import premiumTile3 from '@assets/images/congrats-premium/tile-3.jpg'
import premiumTile4 from '@assets/images/congrats-premium/tile-4.jpg'
import superVideo from '@assets/video/congrats-super/video-1.mp4'
import superPoster from '@assets/images/congrats-super/fallback-1.jpg'
import { useTranslation } from 'react-i18next'

type PlanType = 'premium' | 'super'

type SuccessVariant = {
  headlineKey: string
  subtitleKey: string
  checklistItems: string[]
  ctaKey: string
  titleGradient: string
  checkColor: string
  rightPanelBackground: string
  sources: SlideSource[]
}

const SUCCESS_VARIANTS: Record<PlanType, SuccessVariant> = {
  premium: {
    headlineKey: "You’re now Premium.",
    subtitleKey: 'Ready to create? Here are some ideas:',
    checklistItems: [
      'Create studio-quality product shots',
      'Put your products on real-looking models',
      'Create banners for your online store',
      'Make a sale ad for your social media',
      'Onwards and upwards!',
    ],
    ctaKey: "Let's go",
    titleGradient: premiumBrandGradient,
    checkColor: lightTheme.colors.pink,
    rightPanelBackground: lightTheme.colors.grayScale20,
    sources: [
      { src: premiumTile1 },
      { src: premiumTile2 },
      { src: premiumTile3 },
      { src: premiumTile4 },
    ],
  },
  super: {
    headlineKey: 'You’re now Super.',
    subtitleKey: 'Ready to grow your business? Try these:',
    checklistItems: [
      'Create a complete product photoshoot',
      'Put your products on real-looking models',
      'Create eye-catching product videos',
      'Create banners for your online store',
      'Make a sale ad for your social media',
    ],
    ctaKey: "Let's go",
    titleGradient: superBrandGradient,
    checkColor: lightTheme.colors.superPurple,
    rightPanelBackground: lightTheme.colors.grayScale20,
    sources: [{ src: superVideo, poster: superPoster }],
  },
}


const SuccessCopyColumn = styled('div', {
  ...paddingLonghands(48, 48, 16, 48),
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left',
  width: '50%',
  [COMPACT_MODAL_MEDIA]: { ...COMPACT_PRIMARY_COLUMN_STYLE, paddingTop: '24px' },
})

const SuccessMediaColumn = styled('div', {
  width: '50%',
  position: 'relative',
  overflow: 'hidden',
  [COMPACT_MODAL_MEDIA]: COMPACT_MEDIA_BANNER_STYLE,
})

function TryPremiumSuccess({ planType, close }: { planType: PlanType; close: () => void }) {
  const { t } = useTranslation()
  const variant = SUCCESS_VARIANTS[planType]
  const isSuper = planType === 'super'

  return (
    <>
      <SuccessCopyColumn>
        <h2
          style={{
            margin: 0,
            display: 'inline-block',
            fontSize: '40px',
            fontWeight: 800,
            lineHeight: '46px',
            fontFamily: 'ES-Rebond-Grotesque-Classic, Rubik, Roboto',
            backgroundImage: variant.titleGradient,
            WebkitBackgroundClip: 'text',
            backgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          {t('Congrats!')}
        </h2>
        <h3 style={{ margin: 0, marginTop: '8px', ...lightTheme.typography.Header26bold }}>
          {t(variant.headlineKey)}
        </h3>
        <h4
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            marginBottom: 0,
            marginTop: '24px',
            ...lightTheme.typography.Small14regular,
          }}
        >
          {t(variant.subtitleKey)}
        </h4>
        {variant.checklistItems.map((item, index) => (
          <h4
            key={item}
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '8px',
              marginBottom: 0,
              marginTop: index === 0 ? '24px' : '20px',
              ...lightTheme.typography.Regular16,
            }}
          >
            <Icons.CheckIcon fill={variant.checkColor} /> {t(item)}
          </h4>
        ))}

        <div style={{ marginTop: 'auto', textAlign: 'center' }}>
          <ButtonCustom
            type={SizeButton.LARGE}
            kind={KIND.primary}
            style={{
              position: 'relative',
              ...lightTheme.typography.Small14Semibold,
              color: '#fff',
              marginBottom: '32px',
              marginTop: '8px',
              ...(isSuper ? { background: superBrandGradient } : {}),
            }}
            onClick={close}
          >
            {t(variant.ctaKey)}
          </ButtonCustom>
        </div>
      </SuccessCopyColumn>

      <SuccessMediaColumn style={{ backgroundColor: variant.rightPanelBackground }}>
        <SlidingImages key={planType} sources={variant.sources} />
        <ModalCloseButton onClick={close}>
          <Icons.WhiteCloseIcon size={24} />
        </ModalCloseButton>
      </SuccessMediaColumn>
    </>
  )
}

export default TryPremiumSuccess
